import {fetchCBOR, expovariate, clamp, lerp, range, shuffle, humanFileSize, assert, boxInit, boxAddPoint} from './utils';
import {Clock} from './clock';
import {Alea} from './random';
import {encode as encodeCBOR} from 'cbor-x';
import {vec3} from 'gl-matrix';
import {getCubeDirection} from './cube';

const {floor, round, sqrt, hypot, acos, asin} = Math;

export class Doodle {
    layers = null;
    clock = new Clock;

    constructor(url) {
        this.load(url);
    }

    get layerCount() {
        return this.layers ? this.layers.length : 0;
    }

    async load(url) {
        console.time('doodle-load');
        this.layers = await fetchLayers(url);
        arrangeLayerTimeline(this.layers);
        console.timeEnd('doodle-load');
        this.clock.reset();
    }

    // call to reset the clock after a resum
    resume() {
        this.clock.reset();
    }

    update() {
        if (!this.layers)
            return null;

        const speed = 1;
        const timeDelta = speed * this.clock.update();
        //console.log('doodle:update: %d', round(timeDelta));

        const dabs = [];
        for (const layer of this.layers)
            layer.update(dabs, timeDelta);

        if (dabs.length > 1000) {
            console.warn('doodle: update spawned %d dabs', dabs.length);
        }
        return dabs;
    }
}

class Layer {
    id = 0;
    strokes = [];

    // playback
    time = 0;
    strokeIndex = 0;
    pointIndex = 0;

    constructor(id) {
        this.id = id;
    }

    reset() {
        this.time = 0;
        this.strokeIndex = 0;
        this.pointIndex = 0;
    }

    calcTotalDuration() {
        return this.strokes.reduce((total, stroke) =>
            total + (stroke.duration + stroke.delay),
            0);
    }

    update(out, timeDelta) {
        const numStrokes = this.strokes.length;

        // add to our clock
        this.time += timeDelta;

        let active = false;
        for (;;) {
            // avoid stressing out the paint system at load time,
            // because some layers are scheduled to start at 60% done
            const maxDabsPerUpdate = 300;
            if (out.length > maxDabsPerUpdate) {
                return active;
                // too many
            }

            if (this.strokeIndex >= numStrokes) {
                // run out of strokes
                return active;
            }

            const stroke = this.strokes[this.strokeIndex];

            const {points} = stroke;
            const i = this.pointIndex;
            const pointDuration = points.tSc * points.t[i];

            if (pointDuration > this.time) {
                // not yet
                return active;
            }

            // consume time delta and advance
            this.time -= pointDuration;
            ++this.pointIndex;

            if (this.pointIndex === points.length) {
                // end of stroke: advance & add inter-stroke delay
                this.time -= stroke.delay;
                this.pointIndex = 0;
                ++this.strokeIndex;
            }

            out.push({
                x: points.u[i] / 65535,
                y: points.v[i] / 65535,
                r: points.rSc * points.r[i],
                view: null,
                face: stroke.face,
                brush: stroke.brush,
                color: stroke.color,
                layer: this.id,
                tiltX: points.tx[i] / 127,
                tiltY: points.ty[i] / 127,
                rotate: stroke.angle,
            });
            active = true;
        }
    }
}

function getStrokeAngleFromViewMatrix(matView) {
    const m = matView;
    const scale = 2 / (sqrt(2) * hypot(m[0] + m[2], m[1] + m[3]));
    const t0 = acos(m[0] * scale);
    const t1 = asin(m[1] * scale);
    const t2 = asin(m[2] * -scale);
    const t3 = acos(m[3] * scale);
    return (t0 + t1 + t2 + t3) / 4;
}

function printCborSize(ob, label) {
    const data = encodeCBOR(ob);
    console.log('%s: %s', label, humanFileSize(data.byteLength));
    return data;
}

async function fetchDocumentAsLayers(url) {
    if (0) {
        const blob = await fetch('doodle.cbor.gz').then(r => r.blob());
        const stream = blob.stream().pipeThrough(new DecompressionStream('gzip'));
        console.log(stream);
        const data = await stream.arrayBuffer();
        console.log(data);
    }

    const doc = await fetchCBOR(url);

    printCborSize(doc, 'original');

    let layers = new Map;
    for (const {strokeData} of doc.diffs) {
        const {layerId, brush, color, points} = strokeData;
        let layer = layers.get(layerId);
        if (!layer) {
            const id = layers.size;
            layer = new Layer(id);
            layers.set(layerId, layer);
        }

        const stroke = convertStroke(strokeData);
        layer.strokes.push(stroke);
    }

    layers = Array.from(layers.values());

    if (0) {
        const data = printCborSize(layers, 'layers');
        const filename = 'doodle2.cbor';
        const blob = new Blob([data], {type: 'application/cbor'});
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.click();
        URL.revokeObjectURL(blob);
    }

    return layers;
}

// build a structure of arrays
function SOABuilder() {
    let o = {};
    function push(ob) {
        for (let [k, v] of Object.entries(ob)) {
            if (!o[k])
                o[k] = [];
            o[k].push(v)
        }
    }
    function end(type=Float32Array) {
        let n = -1;
        for (let [k, v] of Object.entries(o)) {
            if (n < 0)
                n = v.length;
            else
                assert(n === v.length);
            o[k] = new type(v);
        }
        o.length = n;
        return o;
    }
    return {push, end};
}

// strip, convert and "compress" stroke data into fixed point
export function convertStroke(strokeData) {
    const faceSize = 1024;
    const pointStride = 9;

    const {points} = strokeData;
    const numPoints = points.length / pointStride;

    let outputPoints = SOABuilder();
    let strokeFace = -1;
    let strokeDuration = 0;
    for (let sp = 0; sp < points.length; sp += pointStride) {
        const x = points[sp + 0];
        const y = points[sp + 1];
        const pressure = points[sp + 2];
        const tiltX = points[sp + 3];
        const tiltY = points[sp + 4];
        const time = points[sp + 5];
        let duration = 0;
        if (sp + pointStride + 5 < points.length)
            duration = (points[sp + pointStride + 5] - time);
        strokeDuration += duration;
        const radius = points[sp + 8] / 2;   // diameter??

        const u = x / faceSize;
        const v = y / faceSize;
        const pointFace = 2*floor(u) + floor(v);
        if (strokeFace < 0) {
            strokeFace = pointFace;
        } else {
            assert(strokeFace === pointFace);
        }

        const r = radius / faceSize;

        outputPoints.push({
            u: u % 1,
            v: v % 1,
            r: r,
            p: pressure,
            tx: tiltX,
            ty: tiltY,
            t: duration,
        });
    }

    outputPoints = outputPoints.end();
    assert(outputPoints.length);

    const outputStroke = {
        delay: clamp(expovariate(200), 50, 1000),
        brush: strokeData.brush,    // could be int 0/1
        color: strokeData.color,
        points: outputPoints,
        face: strokeFace,
        angle: getStrokeAngleFromViewMatrix(strokeData.matView),
        duration: strokeDuration,
    };

    {
        function summarize(arr) {
            let min = Infinity, max = -Infinity, total = 0;
            for (let i = 0; i < arr.length; ++i) {
                const s = arr[i];
                total += s;
                min = Math.min(min, s);
                max = Math.max(max, s);
            }
            return {min, max, mean: total / arr.length};
        }

        // convert to fixed-point
        const src = outputPoints;
        const numPoints = src.length;

        const rMax = summarize(src.r).max;
        const tMax = summarize(src.t).max;

        const out = {
            length: numPoints,
            u: new Uint16Array(numPoints),
            v: new Uint16Array(numPoints),
            r: new Uint8Array(numPoints),
            p: new Uint8Array(numPoints),
            tx: new Int8Array(numPoints),
            ty: new Int8Array(numPoints),
            t: new Uint8Array(numPoints),
            rSc: rMax / 255,
            tSc: tMax / 255,
        };

        for (let i = 0; i < numPoints; ++i) {
            out.u[i] = floor(clamp(src.u[i], 0, 1) * 65535.999);
            out.v[i] = floor(clamp(src.v[i], 0, 1) * 65535.999);
            out.r[i] = floor(clamp(src.r[i]/rMax, 0, 1) * 255.999);
            out.p[i] = floor(clamp(src.p[i], 0, 1) * 255.999);
            out.tx[i] = floor(clamp(src.tx[i], -1, 1) * 127.999);
            out.ty[i] = floor(clamp(src.ty[i], -1, 1) * 127.999);
            out.t[i] = floor(clamp(src.t[i]/tMax, 0, 1) * 255.999);
        }

        // for (let [k, v] of Object.entries(out))
        //     console.log(k, summarize(v));
        outputStroke.points = out;
    }

    return outputStroke;
}

// fetch pre-cooked layers
async function fetchLayers(url) {
    const data = await fetchCBOR(url);
    let total = 0;
    const layers = data.map(({id, strokes}, idx) => {
        const layer = new Layer(id);
        layer.strokes = strokes;
        layer.reset();

        const duration = layer.calcTotalDuration();
        // console.log('%d: %s',
        //     idx,
        //     round(duration * 1e-3));
        total += duration;
        return layer;
    });
    //console.log('total: %s seconds', round(total * 1e-3));
    return layers;
}

function median(arr) {
    arr = arr.toSorted((a, b) => a - b);
    return arr[arr.length >> 1];
}

function calcLayerFace(layer) {
    const faces = layer.strokes.map(s => s.face);
    return median(faces);
}

function calcLayerArea(layer) {
    const box = boxInit();
    for (let stroke of layer.strokes) {
        const {points} = stroke;
        for (let i = 0; i < points.length; ++i) {
            const u = points.u[i] / 65535;
            const v = points.v[i] / 65535;
            boxAddPoint(box, u, v);
        }
    }
    return (box[2] - box[0]) * (box[3] - box[1]);
}

function arrangeLayerTimeline(layers) {
    //console.log('arrangeLayerTimeline: %d %d', layers.length, layers.reduce((a,l) => a+l.strokes.length, 0));

    // calculate face and area for each layer
    const count = new Int32Array(6);
    for (let layer of layers) {
        layer.area = calcLayerArea(layer);
        layer.face = calcLayerFace(layer);
        count[layer.face] ++;
    }

    for (let face = 0; face < 6; ++face) {
        const faceLayers = layers.filter(l => l.face === face);
        // sort by decreasing area
        faceLayers.sort((a, b) => b.area - a.area);
        // arrange with short overlap (eg 1 second)

        let time = 0;
        for (let layer of faceLayers) {
            const duration = layer.calcTotalDuration();
            if (time === 0) {
                // first layer: begin 40% in...
                if (face !== 5) {
                    time = -duration * .4;
                } else {
                    time = 0;
                }
            }
            layer.time = -time;
            time += duration + lerp(1e3, 3e3, Math.random());

            //console.log(duration);
            // console.log('%d: face=%d area=%d',
            //     layer.id,
            //     layer.face,
            //     round(1e4 * layer.area));
        }
    }
}
